<!--

Copyright Paul B. Lucas (C) 2025   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when an authenticated user selects the 
REQUEST LOAN OF EQUIPMENT route on the initial splash page.    The form is designed mobile first.

Setup on the page utilizes authentiation user data to prepopulate several form fields including
name, netid, UIN, home org, and any permission groups.

Actions available on this component for  users are {REVIEW (renders full form), SUBMIT, SAVE (saved - no submittal), CANCEL} 

Revisions:
    02/13/2025      pblucas     initial creation
    03/26/2025      pblucas     chnged assets to list of issued assets and added selector
 
-->
<template>
  <div class="container_01">

    <div class="container_1_0" v-if="renderState == 'THANKS'">
      <!-- thank you message -->
      <h1 class="h1_00">Thank You </h1>
      <p class="p_00">
        You have submitted your request for equipment loan on {{ agreement['date-submitted'] }}.  You can review 
        your loan agreements at any time by clicking 'My Agreements' in the above navigation bar.  Your loan request 
        does not need any further approval.  
      </p>
      <div class="_agreements_controls">
          <button class="_main_button"   type="button" v-if="renderState == 'THANKS'"
              @click="toList(viewIndex)"
              @keydown.enter="toList(viewIndex)">Continue
          </button>
      </div>    

    </div>
    <div class="container_1_0" v-if="renderState == 'INVALID'">
      <!-- invalid submittal message -->
      <h1 class="h1_00">Sorry - Your Submittal is Invalid </h1>
      <p class="p_00">
        You have attempted to submit your loan request, however you have not consented to the agreement policy.
        You must check the boxes in the 'User Compliance and Consent' section of the agreement in order to submit the request.
      </p>
    </div>
    <div class="container_1_0" v-if="renderState == 'NO-ASSETS-REQUESTED'">
      <!-- invalid submittal- no assets to be loaned -->
      <h1 class="h1_00">Sorry - Your Submittal is Invalid </h1>
      <p class="p_00">
        You have attempted to submit your loan request, however you have not selected any assets.
        You must check the boxes issued assets section to specify which assets you are 'borrowing'. 
      </p>
    </div>

    <!-- optional render of agreement in review format (displays additional number and state information)  -->
    <!-- do not attempt to render unless both agreement and employee props are available -->
    <div class="container_1_1" v-if="renderState == 'VIEW'">
    
      <ViewAgreementEntire :agreement="agreement" :key="agreement.uuid" 
                           :employee="user" 
                           :issuedAssets="issuedAssets"   
                           :config="config"  
                           :claims="claims"
                           :gat="gat"/>

    </div>
    <!--  normal rendering for new user form -->
    <div class="container_1_2" v-if="renderState == 'FORM'">
        <h1 class="h1_01">Equipment Loan Agreement</h1>
        <p class="instructions_01">
          The equipment loan policy is located <a href="https://www.busfin.uillinois.edu/bfpp/section-12-property-accounting/loan-equipment-faculty-staff-student-employees" target="_blank">"here"</a>.
        </p>
        <p class="instructions_01">
          <span class="statement">By clicking 'Submit', I certify that I have read and understand the equipment loan policy. 
            I agree that the listed loaned equipment is to be used for University purposes, that I am responsible as listed 
            custodian or steward of the loaned equipment listed hereon, that I will take reasonable care to assure the equipment 
            will not be damaged or lost, and that the loaned equipment will be secured from theft and other loss.  
            I further understand that I may be assessed a liability damage amount for lost or missing equipment.<br /><br />
            I agree that I have completed the security compliance document and that I understand that I am responsible 
            for any data misuse or loss that may occur while the equipment is in my possession.

          </span>
        </p>
        
        <div class="rrw_flag_container" v-if="saveFlag">
          <p class="rrw_p_flags">Saved Agreement</p>
        </div>
              
        <LoanForm :agreement="agreement" 
                  :employee="user" 
                  :issuedAssets="issuedAssets"
                  :claims="claims"  
                  :gat="gat"
                  @homeAddressRequested="setRemoteAddressToHome"   
                  v-if="bRawAgreementLoaded"/>

    </div>
    <!-- redundant to avoid scrolling to top of form -->
    <div class="rrw_flag_container" v-if="saveFlag">
          <p class="rrw_p_flags">Saved Agreement</p>
    </div>
    <div class="rrw_controls" v-if="renderState != 'THANKS' && renderState != 'INVALID' && renderState != 'NO-ASSETS-REQUESTED'">
        <br/>
        <button class="rrw_main_button" type="button" 
            tabindex="0"  
            aria-label="Save agreement"  
            aria-describedby="Control to save agreement for later use"  
            @click="saveAgreement()"
            @keydown.enter="saveAgreement()">Save (for later)</button>
        <button class="rrw_main_button" type="button" 
            tabindex="0"  
            aria-label="Cancel edits to agreement"  
            aria-describedby="Control to cancel edits to agreement and return to pre-edit state"  
            @click="cancel()"
            @keydown.enter="cancel()">Cancel </button>
        <button class="rrw_main_button" type="button"      
            tabindex="0"  
            aria-label="Submit agreement for approval"  
            aria-describedby="Control to submit agreement for approval"  
            @click="submitAgreement()"
            @keydown.enter="submitAgreement()">Submit </button>
        <button class="rrw_main_button"   type="button"  v-if="renderState == 'FORM'"
          tabindex="0"  
          aria-label="View agreement"  
          aria-describedby="Control to view agreement"  
          @click="viewAgreement()"
          @keydown.enter="viewAgreement()">Review Agreement</button>
        <button class="rrw_main_button"   type="button" v-if="renderState == 'VIEW'"
          tabindex="0"  
          aria-label="Edit agreement"  
          aria-describedby="Control to edit agreement"  
          @click="editAgreement()"
          @keydown.enter="editAgreement()">Edit</button>
        <button class="rrw_main_button" type="button"  v-if="renderState == 'VIEW'"
          tabindex="0"  
          aria-label="Create PDF of page"  
          aria-describedby="Control to generate, download, and display in alternate tab a PDF"  
          @click="generatePDF()"
          @keydown.enter="generatePDF()">Create a PDF </button>
    </div>
    <div class="rrw_controls" v-if="renderState == 'INVALID'">
        <button class="rrw_main_button"   type="button" 
          tabindex="0"  
          aria-label="Edit agreement"  
          aria-describedby="Control to edit agreement and continue after invalid entry"  
          @click="saveAndRedit()"
          @keydown.enter="saveAndRedit()">Continue</button>
    </div>
    <div class="rrw_controls" v-if="renderState == 'NO-ASSETS-REQUESTED'">
        <button class="rrw_main_button"   type="button" 
          tabindex="0"  
          aria-label="Edit agreement"  
          aria-describedby="Control to edit agreement and continue after no-asset entry"  
          @click="editAgreement()"
          @keydown.enter="editAgreement()">Continue</button>
    </div>

    <!--   breaks to allow footer space -->
    <br/><br/>
  </div>    
</template>

<script>
import {ref, computed}            from 'vue'
import {useStore}                 from 'vuex'
import LoanForm                   from  '@/components/Agreements/LoanForm.vue'
import ViewAgreementEntire        from  '@/components/Agreements/ViewAgreementEntire.vue'

import useAgreementsAPI           from  '@/composables/useAgreementsAPI'  
import useAssetsAPI               from  '@/composables/useAssetsAPI' 
import useEmployeesAPI            from  '@/composables/useEmployeesAPI'
import useRefreshAPI              from  '@/composables/useRefreshAPI'
import usePermissionsAPI          from  '@/composables/usePermissionsAPI'

// import enumerations
import SysConfigs                 from '@/configuration/form-options.json'

// pdf converter  install as npm install --save dom-to-pdf
import domToPdf                     from 'dom-to-pdf'

export default {
  name: 'requestEquipmentLoan',
  components: {
    LoanForm,
    ViewAgreementEntire  
  },
  props: [
    'claims',
    'gat'
  ],
  emits: [      
  ],
  setup(props) {
      /*
        The loan agreement utilizes a combo of employee record data, including contacts,
        and agreement data, including loaned assets.   Not all props are supported by
        this form.  This form replicates parts of the Qualtrics form.

        The form also uses per-org configs established on the user's college, department, orgcode, or
        home-org to establish department/unit level policies.
  
        Note:  when declaring objects, do not declare autogenerated properties such as uuid
               or record-created-on dates.  

        SETUP PROCESS:
          1.  Declare refs and private methods
          2.  Determine if the store.state.config is a fetched value or a default, and if default,
              attempt to reload config based on browser storage.
          3.  Define object prototypes for contacts and assets
          4.  Determine if store.state.user is fetched value of default, and if default, reload
              user from browser stored netid/token
          5.  construct prototype agreement object for use
          6.  Load issued assets for the employee
          7.  Clone agreement object into non-reactive backupAgreement object
  
      */
      const store                           =   useStore()
      const { checkStoreForRefresh }        =   useRefreshAPI()
      const { checkAdminGroups }            =   usePermissionsAPI()   
      const { assetResults, assetKeyData, fetchAssetsOnQuery, storeAsset} = useAssetsAPI()

      // there are refs and must be unwrapped with .value
      const user                  = computed( () => store.state.user)
      const config                = computed( () => store.state.config)   
 
      // these need to store the user also
      const addContact          = (contact)     =>  store.dispatch('addContact', contact)
      const deleteContact       = (idx)         =>  store.dispatch('deleteContact', idx)
      const setUser             = (u)           =>  store.dispatch('setUser', u)

  
      // principle objects defined here
      const agreement           = ref({})
      const issuedAssets        = ref([])                    // assets for which this user is steward
      const backupAgreement     = ref({})                    // for cancel operations
      const backupUser          = ref({})                    // also for cancel ops

      // handle enumerations from configuration data
      const sysConfigs          = ref([])
      sysConfigs.value          = SysConfigs 
   
      const bRawAgreementLoaded = ref(false)



      // refer to contacts.class.inc.php
      const contactItem   = ref({
        'type':                     '',                       // see type enums from Configs
        'handle':                   '',
        'value':                    '',
        'reference':                ''
     })

      // MARK: Entry processing for user based on user object in store
      // See if the user is an employee in the database, if so use that employee for
      // the agreement generation since this is a request.   If in database, get employee, if
      // not, check the AITS person database (temporarily just create an employee with
      // usable data for development).  Then set employee_fk on agreement to employee id (Pkey)
      // and store a prototype agreement.  The process is:
      //    store employee
      //    refetch employee   (obtains id and uuid)
      //    set fks on agreement
      // Note: agreement is not stored at this time- just a class instance prototype
      //

      // MARK:  refresh store from database if global browser refresh fubars the store
      checkStoreForRefresh()
      checkAdminGroups(props.claims, store)

      // construct the pending loan agreement using pre-populated elements from user claims   
      agreement.value = {
          // agreement data (see agreement.class.inc.php)
          "fk-employee-id":           user.value.id,                  // FK to employee record                  
          "employee-name":            user.value['name-concat'],  
          "applicable-org":           user.value['home-org'],
          "validity-period":          "",
          "date-submitted":           "",
          "agreement-notes":          "",
          "loan-reason":              "",
          "asset-address1":           "",
          "asset-address2":           "",
          "asset-city":               "",
          "asset-state":              "",
          "asset-postcode":           "",
          "asset-country":            "United States",
          "policy-consent":           false,
          "security-consent":         false,
          // subordinate array props   
          "loaned-assets":            [],                         // array of remote asset objects
      }

      // fetch assets issued to employee (on netid) or loaned prior
      let agreementCheckedAssets = []
      fetchAssetsOnQuery({"steward-netid": user.value.netid, "status": "Active-Issued"})
      .then( async () => {

          // assets issued to employee not previously loaned/checked as loan
          // add selection property to array  
          issuedAssets.value = assetResults.value.map(o => ({...o, ...{"selected": false}}))

          // fetch assets issues marked as selected (for loan)
          await fetchAssetsOnQuery({"steward-netid": user.value.netid, "status": "Active-Loaned"})
          .then ( () =>   { 
              agreementCheckedAssets = assetResults.value.map(o => ({...o, ...{"selected": true}}))
              // transfer them to the issued list with the selection prop
              agreementCheckedAssets.forEach( (a) =>  {
                  issuedAssets.value.push(a)
              })
          })                           
      })


      // copy for cancel operations
      backupAgreement.value     = JSON.parse(JSON.stringify(agreement.value))
      backupUser.value          = JSON.parse(JSON.stringify(user.value))  

      bRawAgreementLoaded.value = true
   
      // use the following in this. context
      return {user, agreement, backupAgreement, backupUser, contactItem,  
              sysConfigs, config, bRawAgreementLoaded, 
              deleteContact, addContact, setUser, issuedAssets }
  },
  data () {
    // use in this. context
    return {
   
      renderState:  "FORM",
      saveFlag: false,
       
    }
  },
  beforeMount() {
  },
  mounted()   {
  },
  updated()   {
  },
  methods: {

    // FUNCTION to copy home address on user to workplace remote site address
    setRemoteAddressToHome(ev)  {
        this.agreement['asset-address1']    = this.user?.['address1'] ?? ""
        this.agreement['asset-address2']    = this.user?.['address2'] ?? ""
        this.agreement['asset-city']        = this.user?.['city'] ?? ""
        this.agreement['asset-state']       = this.user?.['state'] ?? ""
        this.agreement['asset-postcode']    = this.user?.['postcode'] ?? ""
        this.agreement['asset-country']     = this.user?.['country'] ?? "United States"
    },

    //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    //  MARK: METHODS TO HANDLE FORM WHEN COMPLETED
    //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

    // FUNCTION: takes the agreement on the list using the v-for indices and renders entire form
    // Function is member of action list with (agree, idx) argument
    viewAgreement() {

        this.agreement['loaned-assets'] = []

        // push assets marked to loan agreement issued assets array
        this.issuedAssets.forEach( (a) => {
            if(a.selected) {
                a.status = 'Active-Loaned'    
            }
            else {
                a.status = 'Active-Issued'
            }
            this.agreement['loaned-assets'].push(a)
            
        })

        // set agreement and copy (non-reactive) for backup operation
        this.backupAgreement    =   JSON.parse(JSON.stringify(this.agreement))
        this.saveFlag           =   false
        this.renderState        =   "VIEW"
    },

    // FUNCTION: Cancels and backs out any changes made to agrement
    cancel()   {
      // return form to pristine state and disregard edits
      //console.log("RRW:: Cancel fired")
      this.agreement      =   this.backupAgreement
      this.setUser(this.backupUser)
      this.renderState    =   'FORM'
      this.saveFlag       =   false

    },
    async submitAgreement() {

      let dt = new Date()

      // push assets marked to loan agreement issued assets array
      this.issuedAssets.forEach( (a) => {
          if(a.selected) {
              a.status = 'Active-Loaned'    
          }
          else {
              a.status = 'Active-Issued'
          }
          this.agreement['loaned-assets'].push(a)
          
      })
 
      // default validity period to two months if not set
      if(this.agreement['validity-period'].length == 0  )
          this.agreement['validity-period'] = 24

      if(   (this.agreement['policy-consent'] == false)  
         || (this.agreement['security-consent'] == false) ) {
        this.renderState = 'INVALID'        
      }
      else if (this.agreement['loaned-assets'].length == 0) {
        this.renderState = 'NO-ASSETS-REQUESTED'
      }
      else {
        // set submittal date
        this.agreement['date-submitted'] = dt.toDateString()

        await this.saveAgreement()
        .then( () => {
          this.saveFlag = false
          this.renderState = 'THANKS' 
        })
      }
    },
    toList() {
        this.saveFlag = false
        this.renderState = 'LIST' 
        this.$router.push({name: "listMyAgreements"}) 
    },
    // Recovery from invalud submittal - save existing changed and redit
    async saveAndRedit(idx) {
        await this.saveAgreement(idx)
        .then( () => {
            this.saveFlag      =   false
        })      
    },

    // FUNCTION:  Saves agreement as entered but does not submit
    async saveAgreement()   {
 
      // NOTE:  must use this. form verses .value form for refs in setup
      // composables return references accessible by .value
      const {agreeResults, agreeKeyData, fetchAgreementsOnQuery, storeAgreement} =  useAgreementsAPI()
      const { emplResults, emplKeyData, storeEmployee, fetchEmployeesOnQuery  } =   useEmployeesAPI()
      const { checkAdminGroups }                                                =   usePermissionsAPI()   

      this.agreement['applicable-org'] = this.user['home-org']

      // push assets marked to loan agreement issued assets array
      this.issuedAssets.forEach( (a) => {
          if(a.selected) {
              a.status = 'Active-Loaned'    
          }
          else {
              a.status = 'Active-Issued'
          }
          this.agreement['loaned-assets'].push(a)
          
      })
      // default validity term
      if(this.agreement['validity-period'].length == 0  )
          this.agreement['validity-period'] = 24

      await storeAgreement(this.agreement)
      .then(async () => {
          await fetchAgreementsOnQuery({id: agreeKeyData.value.results[0].pkey})
          .then( () => {
 
              //console.log("Fetched Agreement: "+JSON.stringify(agreeResults.value[0]))
              this.agreement        = agreeResults.value[0]      
              this.renderState      = 'FORM'
          })
      })
      .then( async () => {
          // work around store mutation
          let userToMutate = JSON.parse(JSON.stringify(this.user))

          await storeEmployee(userToMutate)
          .then( async () => {
              await fetchEmployeesOnQuery({id: emplKeyData.value.results[0].pkey})
              .then( () => {
                  this.setUser(emplResults.value[0])   
                  checkAdminGroups(this.claims)
                  this.renderState      = 'FORM'
                            
              })
          })
      }).finally( () => {
          this.saveFlag         =   true   
      })

    
    },   

    // FUNCTION:  Toggles agreement to full agreement view
    async reviewAgreement()   {
      //console.log("RRW:: Review agreement fired")

      // NOTE:  must use this. form verses .value form for refs in setup
      // composables return references accessible by .value
      const {agreeResults, agreeKeyData, fetchAgreementsOnQuery, storeAgreement} = useAgreementsAPI()

      // invokes full view or agreement and employee
      // Note: Since some agreement and employee properties are constructed by the database constructor
      // on storeObject(), we need to first store then refetch the object.  Must be done as a promise

      // need to update the database with what we have so far to ensure construction values are set
      this.agreement['employee-fk]']  = this.user.id  
      this.agreement['aplicable-org'] = this.user['home-org']

      // push assets marked to loan agreement issued assets array
      this.issuedAssets.forEach( (a) => {
          if(a.selected) {
              a.status = 'Active-Loaned'    
          }
          else {
              a.status = 'Active-Issued'
          }
          this.agreement['loaned-assets'].push(a)
          // since we are reviewing - not editing just show loaned
          this.issuedAssets = this.agreement['loaned-assets']
          
      })  
      await storeAgreement(this.agreement)
      .then( async () => {
          await fetchAgreementsOnQuery({id: agreeKeyData.value.results[0].pkey})
          .then( () => {
              //console.log("Fetched Agreement: "+JSON.stringify(agreeResults.value[0]))
              this.agreement = agreeResults.value.results[0]         
              this.renderState    = 'VIEW'   
          })
      })
    },

    //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    //  MARK: Additional functions available from VIEW page
    //  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    editAgreement()   {
        this.renderState   =  'FORM'
       
    },
 
    // FUNCTION: Created a PDF of ViewAgreementEntire rendering
    // id of element on DOM is '_pdf_render'
    // Need to created from the DOM and then do a standard download so that the PDF appears
    // in the browser downloads.
    async generatePDF() {
        
        // MARK:  generate the appropriate filename with embedded date
        const date = new Date()
        let year    =   date.getFullYear().toString()
        let month   =   date.getMonth()+1
        month       =   month.toString().padStart(2,'0')
        let day     =   date.getDate()
        day         =   day.toString().padStart(2,'0')
        let hours   =   date.getHours().toString().padStart(2,'0')
        let mins    =   date.getMinutes().toString().padStart(2,'0')

          // 20240404_1559 format
          const filedate = year+month+day+'_'+hours+mins
          let agreeNumber = this.agreement['number']
        
        // MARK: - Since this is a list of multiple employees, we want to 
        // identify the agreement document with the employee's last name in
        // addition to the agreement number.  This is available in string form and we
        // want only the last name
        let aNames = this.agreement['employee-name'].split(" ")
        let lName = aNames[aNames.length-1]
        const pdfFileName = lName+"-Work-Agreement-"+agreeNumber+"-"+filedate+".pdf"    

        // select and the container and add a title for teh PDF document              
        var element = document.getElementById('_pdf_render');
        var title   = document.createElement("p")
        var brk     = document.createElement("br")
        title.textContent = "Remote Work Agreement"
        title.style['font-family'] = "'Times New Roman',Times, serif"
        title.style['font-size'] = "16pt"
        title.style['font-weight'] = "bold"
        title.style['text-align'] = "center"
        title.appendChild(brk)
        title.appendChild(brk)
        var firstChild = document.getElementById("_pdf_row1")
        element.insertBefore(title,firstChild)

        var options = {
            filename: pdfFileName,
            excludeTagNames: ['button']
        };
        await domToPdf(element, options, function(pdf) {
              
            title.remove()
        });
    }
  }
}
</script>

<style scoped>

.container_01 {
  font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
  scrollbar-gutter: stable both-edges;
  margin: 25px 0 0 25px;
  padding: 0;
}

.container_1_0 {
  width: 100%;
  margin: 0 auto; 
  padding: 25px 0;
}
.container_1_1 {
  width: 98%;
  margin: 0 auto;
}
.container_1_2 {
  width: 100%;
  margin: 0 auto;
}

.h1_00 {
  color: red;
  font-size: 16pt;
  text-align: center;
}
.h1_01 {
  color: #13294B;
  font-size: 16pt;
  text-align: center;
}
.instructions_01 {
  width: 90%;
  margin: 20px auto;
  text-align: justify;
}
.p_00 {
  max-width: 60%;
  text-align: justify;
  margin: 2px auto;
  border: 1px;
  border-color: black;

}
.statement {
  font-style: italic;
}

.rrw_controls {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
  padding: 5px;
}

.rrw_main_button {
  background-color: #13294B;
  color: white;
  font-weight: bold;
  border-style: solid;
  border-radius: 5px;
  border-width: 1px;
  border-color: #13294B;
  margin: 5px 10px;
  padding: 5px;
}
.rrw_main_button:hover {
  background-color: #FF5F05;
  color: white;
}

.rrw_flag_container {
  width: 15%;
  padding: 5px;
  float: right;
}
.rrw_p_flags {
  font-size: 14pt;
  font-weight: bold;
  color: #FF5F05;
}
a {
  color: #13294B;
  text-decoration: ione;
}
a:hover {
  color: #FF5F05;
  text-decoration: none;
}
._agreements_controls {
        display: flex;
        align-items: enter;
        justify-content: center;
        margin: 0;
        padding: 5px;
}
._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
    padding: 5px;
}
._main_button:hover {
    background-color: #FF5F05;
    color: white;
}

</style>
